import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../useApiMutation';
import { invoiceKeys } from '@/lib/queryKeys';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { apiPost, operationsClient } from '@/lib/api/client';
import { normalizeInvoice } from './useInvoice';
import { reportDelivery, INVOICE_REMINDER_DELIVERY_COPY } from './reportDelivery';
import type { Invoice, InvoiceDeliveryOutcome, SendInvoiceDto } from '@/types/invoice';

type RemindedInvoice = Invoice & { delivery?: InvoiceDeliveryOutcome };

/**
 * "Send for approval" is DRAFT-only, so this is the only resend affordance an
 * operator has on an OPEN invoice. It used to toast a flat "Invoice reminder
 * sent" on every 200 — including the ones where the provider rejected the
 * address — so route the server's `delivery` outcome through the same reporter
 * `useSendInvoice` uses rather than assuming success.
 */
export function useRemindInvoice() {
  const queryClient = useQueryClient();

  return useApiMutation<RemindedInvoice, SendInvoiceDto>({
    mutationFn: async ({ id, ...body }) => {
      const raw = await apiPost<Record<string, unknown>, Omit<SendInvoiceDto, 'id'>>(
        operationsClient,
        invoiceEndpoints.remind(id),
        body,
      );
      // `normalizeInvoice` only maps known invoice fields, so pull `delivery`
      // off the raw response before it is dropped.
      return {
        ...normalizeInvoice(raw),
        delivery: raw.delivery as InvoiceDeliveryOutcome | undefined,
      };
    },
    onSuccess: (data) => {
      queryClient.setQueryData<Invoice>(invoiceKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all() });
      reportDelivery(data.delivery, INVOICE_REMINDER_DELIVERY_COPY);
    },
  });
}
